import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from 'primereact/checkbox';
import { useCallback, useEffect, useRef, useState } from "react";
import ProductService from "../../services/products/products.service";
import { ProductDetailModel, ProductImageItem } from "../../components/admin/products";
import { Toast, ToastMessage } from "primereact/toast";
import { InputNumber } from 'primereact/inputnumber';
import vendorService from "../../services/vendors/vendor.service";
import { VendorSummaryDTO } from "../../models/vendors/vendor-model";
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    horizontalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface EditProductProps {
    isUpdate: boolean;
    hideTitle?: boolean;
    handleOnSuccess: any;
    updateProductInfo: ProductDetailModel | undefined | null;
    subCate?: any;
}

interface UploadedImage {
    id: number;
    serverId?: number;
    file?: File;
    previewUrl: string;
    isDefaultMedia: boolean;
    isDelete?: boolean;
    sequenceNo?: number;
}

interface CropModalState {
    imageId: number;
    srcUrl: string;
    crop: Crop;
}

const SortableImageItem = ({
    img,
    handleDeleteImage,
    handleSetDefault,
    openCropModal
}: { img: UploadedImage, handleDeleteImage: any, handleSetDefault: any, openCropModal: any }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: img.id.toString() });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                border: img.isDefaultMedia ? '1px solid #4caf50' : '1px solid #e9ecef',
                borderRadius: '12px',
                overflow: 'hidden',
                background: '#ffffff',
                boxShadow: isDragging
                    ? '0 8px 24px rgba(0,0,0,0.12)'
                    : img.isDefaultMedia
                        ? '0 0 0 1px #4caf50'
                        : '0 2px 8px rgba(0,0,0,0.04)',
                opacity: isDragging ? 0.8 : 1,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Image area — position:relative so overlays stack correctly */}
            <div style={{ position: 'relative', width: '100%', height: '160px', background: '#f8f9fa', flexShrink: 0 }}>

                {/* Product image */}
                <img
                    src={img.previewUrl}
                    alt={img.file?.name ?? 'product'}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                        padding: '10px',
                        filter: isDragging ? 'grayscale(30%)' : 'none',
                        transition: 'filter 0.2s',
                        pointerEvents: 'none',
                    }}
                />

                {/* Drag handle — top left */}
                <div
                    {...attributes}
                    {...listeners}
                    title="Drag to reorder"
                    style={{
                        position: 'absolute', top: '8px', left: '8px',
                        background: 'rgba(255,255,255,0.95)',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        width: '28px', height: '28px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'grab',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                        zIndex: 2,
                    }}
                >
                    <i className="pi pi-bars" style={{ fontSize: '0.8rem', color: '#6c757d' }} />
                </div>

                {/* Action buttons — top right */}
                <div style={{
                    position: 'absolute', top: '8px', right: '8px',
                    display: 'flex', gap: '6px', zIndex: 2,
                }}>
                    {/* Crop — new uploads only */}
                    {img.id < 0 && (
                        <button
                            type="button"
                            title="Crop image"
                            onClick={() => openCropModal(img)}
                            style={{
                                background: 'rgba(255,255,255,0.95)',
                                border: '1px solid #e9ecef',
                                borderRadius: '6px',
                                width: '28px', height: '28px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#339af0',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                padding: 0,
                            }}
                        >
                            <i className="pi pi-pencil" style={{ fontSize: '0.8rem' }} />
                        </button>
                    )}
                    {/* Delete */}
                    <button
                        type="button"
                        title="Remove image"
                        onClick={() => handleDeleteImage(img.id)}
                        style={{
                            background: 'rgba(255,255,255,0.95)',
                            border: '1px solid #e9ecef',
                            borderRadius: '6px',
                            width: '28px', height: '28px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: '#fa5252',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            padding: 0,
                        }}
                    >
                        <i className="pi pi-trash" style={{ fontSize: '0.8rem' }} />
                    </button>
                </div>
            </div>

            {/* Footer: Set as Default */}
            <div style={{
                padding: '10px 14px',
                background: '#ffffff',
                borderTop: '1px solid #f1f3f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    color: img.isDefaultMedia ? '#4caf50' : '#868e96',
                    fontWeight: img.isDefaultMedia ? 600 : 500,
                    userSelect: 'none',
                    margin: 0,
                }}>
                    <input
                        type="radio"
                        name={`defaultMedia_${img.id}`}
                        checked={img.isDefaultMedia}
                        onChange={() => handleSetDefault(img.id)}
                        style={{
                            accentColor: '#4caf50',
                            cursor: 'pointer',
                            width: '14px', height: '14px',
                            margin: 0,
                        }}
                    />
                    Set as Default
                </label>
            </div>
        </div>
    );
};


const ProductInfo: React.FC<EditProductProps> = ({ isUpdate, hideTitle, handleOnSuccess, updateProductInfo, subCate }) => {
    const { t } = useTranslation();
    const toast = useRef<Toast>(null);
    const [categories, setCategories] = useState<any>();
    const [subCategories, setSubCategories] = useState<any>(subCate);
    const [selectedCategory, setSelectedCategory] = useState<any>();
    const [selectSubCategory, setSelectedSubCategory] = useState<any>();
    const [filteredSubCategories, setFilteredSubCategories] = useState<any>();
    const [mesuretype, setMessuretype] = useState<string>("");
    const [vendorList, setVendorList] = useState<{ label: string; value: number }[]>([]);
    const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [cropModal, setCropModal] = useState<CropModalState | null>(null);
    const [isCropLoading, setIsCropLoading] = useState(false);
    const cropImgRef = useRef<HTMLImageElement | null>(null);
    const completedCropRef = useRef<PixelCrop | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
        toast.current?.show({ severity: severity, summary: label, detail: message, life: 3000 });
    }

    const formik: any = useFormik({
        initialValues: {
            name: "",
            vendorID: null,
            categoryID: null,
            subCategoryID: null,
            code: "",
            weight: "",
            description: "",
            usage: "",
            minOrderQty: "",
            unitPrice: "",
            gst: "",
            hsN_Code: "",
            subscriptionAvailable: false,
            activeStartDate: null,
            activeEndDate: null,
            leadTime: "",
            canExchange: false,
            canReturn: false,
            courierAvailable: false,
            shelfLife: "",
            inStock: 1,
            isDeleted: false,
            isHomeDeliveryAvailable: false,
            forCustomer: false,
            orderMultiples: "",
            volume: "",
            measurement: ""
        },
        validateOnChange: false,
        validate: () => ({}),   // validation handled via toast in onSubmit
        onSubmit: async (data: any) => {
            // ── Toast-based validation ──
            const missing: string[] = [];

            if (!data.categoryID) missing.push('Category');
            if (!data.subCategoryID) missing.push('Sub Category');
            if (!data.vendorID) missing.push('Vendor');
            if (!data.name) missing.push('Product Name');
            if (!data.measurementTypeID) missing.push('Measurement Type');
            if (!data.measurement) missing.push('Measurement Unit (ml/gms)');
            if (data.volume === null || data.volume === undefined || data.volume === '') missing.push('Volume');
            if (data.unitPrice === null || data.unitPrice === undefined || data.unitPrice === '') missing.push('Unit Price');
            if (data.weight === null || data.weight === undefined || data.weight === '') missing.push('Weight');
            if (data.minOrderQty === null || data.minOrderQty === undefined || data.minOrderQty === '') missing.push('Min Order Qty');
            if (!data.orderMultiples) missing.push('Order Multiples');
            if (data.gst === null || data.gst === undefined || data.gst === '') {
                missing.push('GST');
            } else if (!/^-?\d{1,2}(\.\d{1,2})?$/.test(data.gst)) {
                toast.current?.show({ severity: 'error', summary: 'Invalid GST', detail: 'GST must be a number with up to 2 digits (e.g. 18 or 5.5).', life: 4000 });
                return;
            }
            if (data.activeStartDate && data.activeEndDate) {
                if (new Date(data.activeStartDate) > new Date(data.activeEndDate)) {
                    toast.current?.show({ severity: 'error', summary: 'Invalid Dates', detail: 'Active End Date cannot be before Active Start Date.', life: 4000 });
                    return;
                }
            }

            if (!data.courierAvailable && !data.isHomeDeliveryAvailable) {
                toast.current?.show({ severity: 'error', summary: 'Delivery Option Required', detail: 'Please select at least one delivery option: Courier Available or Home Delivery Available.', life: 4000 });
                return;
            }

            if (missing.length > 0) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Required Fields Missing',
                    detail: `Please fill in: ${missing.join(', ')}.`,
                    life: 6000,
                });
                return;
            }

            const formData = new FormData();

            formData.append("ProductID", data.productID || "");
            formData.append("VendorID", data.vendorID);
            formData.append("SubCategoryID", data.subCategoryID);
            formData.append("MeasurementTypeID", data.measurementTypeID);
            formData.append("Name", data.name);
            formData.append("UnitPrice", data.unitPrice);
            formData.append("Code", data.code || "");
            formData.append("Description", data.description || "");
            formData.append("Usage", data.usage || "");
            formData.append("Weight", data.weight);
            formData.append("MinOrderQty", data.minOrderQty);
            formData.append("OrderMultiples", data.orderMultiples || "");
            formData.append("GST", data.gst);
            formData.append("HSN_Code", data.hsN_Code || "");
            formData.append("SubscriptionAvailable", String(data.subscriptionAvailable === true));
            if (data.activeStartDate) formData.append("ActiveStartDate", data.activeStartDate);
            if (data.activeEndDate) formData.append("ActiveEndDate", data.activeEndDate);
            if (data.leadTime) formData.append("LeadTime", data.leadTime);
            formData.append("CanExchange", data.canExchange);
            formData.append("CanReturn", data.canReturn);
            formData.append("CourierAvailable", data.courierAvailable);
            formData.append("ShelfLife", data.shelfLife || "");
            formData.append("InStock", data.inStock ?? "");
            formData.append("IsDeleted", String(data.isDeleted));
            formData.append("Volume", data.volume ?? "");
            formData.append("Measurement", data.measurement || "");
            formData.append("IsHomeDeliveryAvailable", String(data.isHomeDeliveryAvailable));
            formData.append("ForCustomer", data.forCustomer);
            formData.append("ContainerType", "ProductImages");
            // Send all images as one unified indexed list — maps to List<ProductImageDto> on the backend
            uploadedImages.forEach((img, index) => {
                formData.append(`ProductImageList[${index}].Id`, String(img.id != 0 ? img.id : 0));
                formData.append(`ProductImageList[${index}].IsDefault`, String(img.isDefaultMedia));
                formData.append(`ProductImageList[${index}].IsDeleted`, String(img.isDelete ?? false));
                // Binding SequenceNo strictly to the item's current index directly reflects their Drag-and-Drop order
                formData.append(`ProductImageList[${index}].SequenceNo`, String(index + 1));

                if (img.id < 0 && img.file) {
                    // New upload: attach the actual file
                    formData.append(`ProductImageList[${index}].ProductImage`, img.file);
                    // Appending 'productImages' alongside for backward compatibility in POST
                    if (!isUpdate) {
                        formData.append('productImages', img.file);
                    }
                } else {
                    // Existing server image: attach a 0-byte dummy Blob to satisfy the backend [Required] validation
                    formData.append(`ProductImageList[${index}].ProductImage`, new Blob([''], { type: 'image/jpeg' }), 'existing.jpg');
                }
            });

            let response = isUpdate ? await ProductService.saveProduct(formData) : await ProductService.saveNewProduct(formData);

            if (response.isSuccess) {
                handleOnSuccess();
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            } else {
                showMessage('Update Products', response.message + ': Error in update product', 'error');
            }
        }
    });

    useEffect(() => {
        fetchCategory();
        fetchSubCategory();
        fetchVendors();
    }, []);

    useEffect(() => {
        if (isUpdate && updateProductInfo && subCategories.length > 0) {
            setProductData();
        }
    }, [isUpdate, updateProductInfo, subCategories]);

    function fetchCategory() {
        ProductService.getCategory().then((res) => {
            setCategories(res);
        });
    }

    function fetchSubCategory() {
        ProductService.getSubCategory().then((res) => {
            setSubCategories(res);
        })
    }

    const fetchVendors = () => {
        vendorService.getAllVendors().then((res: VendorSummaryDTO[]) => {
            const mapped = res.map((v) => ({
                label: `${v.firmInfo}${v.officeContactNbr ? `, ${v.officeContactNbr}` : ""}`,
                value: v.vendorID
            }));
            setVendorList(mapped);
        });
    };

    function setProductData() {
        if (updateProductInfo) {
            let categoryId: number = updateProductInfo.categoryID;
            setSelectedCategory(categoryId);
            if (subCategories && subCategories.length > 0) {
                const filterData: any = subCategories.filter((eachItem: any) => eachItem.categoryID === categoryId)
                setFilteredSubCategories(filterData);
            }

            // Load existing server images into the uploadedImages preview state
            const imageList: ProductImageItem[] = Array.isArray(updateProductInfo.productImageList)
                ? updateProductInfo.productImageList
                : [];

            if (imageList.length > 0) {
                // Sort the images by sequenceNo ascending (fallback to 0 if undefined) so the preview renders in the correct order
                const sortedImageList = [...imageList].sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0));

                const existingImages: UploadedImage[] = sortedImageList.map((item) => ({
                    id: item.id,   // string id
                    serverId: item.id,            // keep the real numeric DB id
                    previewUrl: item.mediaUrl,
                    isDefaultMedia: item.isDefault,
                    isDelete: item.isDeleted,
                    sequenceNo: item.sequenceNo,
                }));
                // Ensure exactly one visible default; fallback to first visible if none marked
                const visibleImages = existingImages.filter(img => !img.isDelete);
                if (visibleImages.length > 0 && !visibleImages.some(img => img.isDefaultMedia)) {
                    const firstVisible = visibleImages[0];
                    existingImages.forEach(img => {
                        img.isDefaultMedia = (img.id === firstVisible.id);
                    });
                }
                setUploadedImages(existingImages);
            }

            formik.setValues({
                productID: updateProductInfo.productID,
                name: updateProductInfo.name ?? "",
                vendorID: updateProductInfo.vendorID,
                categoryID: updateProductInfo.categoryID,
                subCategoryID: updateProductInfo.subCategoryID,
                measurementTypeID: updateProductInfo.measurementTypeID,
                code: updateProductInfo.code ?? "",
                weight: updateProductInfo.weight ?? "",
                description: updateProductInfo.description ?? "",
                usage: updateProductInfo.usage ?? "",
                minOrderQty: updateProductInfo.minOrderQty ?? "",
                unitPrice: updateProductInfo.unitPrice ?? "",
                gst: updateProductInfo.gst ?? "",
                hsN_Code: updateProductInfo.hsN_Code ?? "",
                subscriptionAvailable: updateProductInfo.subscriptionAvailable ?? false,
                activeStartDate: updateProductInfo.activeStartDate,
                activeEndDate: updateProductInfo.activeEndDate,
                leadTime: updateProductInfo.leadTime ?? "",
                canExchange: updateProductInfo.canExchange,
                canReturn: updateProductInfo.canReturn,
                courierAvailable: updateProductInfo.courierAvailable,
                shelfLife: updateProductInfo.shelfLife ?? "",
                inStock: updateProductInfo.inStock ?? 1,
                isDeleted: updateProductInfo.isDeleted,
                isHomeDeliveryAvailable: updateProductInfo.isHomeDeliveryAvailable,
                forCustomer: updateProductInfo.forCustomer ?? false,
                orderMultiples: updateProductInfo.orderMultiples ?? "",
                volume: updateProductInfo.volume ?? "",
                measurement: updateProductInfo.measurement ?? ""
            });
        }
    }

    const measurementTypeItems = [
        { "name": "Litre", "measurementTypeID": 1 },
        { "name": "Kilogram", "measurementTypeID": 2 },
        { "name": "Grams", "measurementTypeID": 3 },
        { "name": "Pieces", "measurementTypeID": 4 }
    ];

    const isFormFieldInvalid = (name: any) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name: any) => {
        return isFormFieldInvalid(name) ? <small className="p-error">{formik.errors[name]}</small> : "";
    };

    const categoryOnChange = (e: DropdownChangeEvent) => {
        setSelectedCategory(e.value);
        const filterSubCategories = subCategories.filter((eachItem: any) => eachItem.categoryID == e.value);
        setFilteredSubCategories(filterSubCategories);
    }

    const subCategoryOnChange = (e: DropdownChangeEvent) => {
        setSelectedSubCategory(e.value);
    }

    const measureTypeOnChange = (e: DropdownChangeEvent) => {
        const selectedItem = measurementTypeItems.find(item => item.measurementTypeID === e.value);
        if (selectedItem) {
            setMessuretype(selectedItem.name);
        }
    };



    const addImageFiles = useCallback(async (files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

        const validFiles = fileArray.filter(f => supportedTypes.includes(f.type.toLowerCase()));

        if (fileArray.length > validFiles.length) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Unsupported Format',
                detail: 'Only JPEG, PNG and WebP images are accepted.',
                life: 4000
            });
        }

        if (validFiles.length === 0) return;

        // Map original files directly to uploaded images state
        const newImages: UploadedImage[] = validFiles.map((file, idx) => ({
            id: -(Date.now() + idx),
            file: file,
            previewUrl: URL.createObjectURL(file),
            isDefaultMedia: false,
            isDelete: false,
        }));

        setUploadedImages(prev => {
            const combined = [...prev, ...newImages];
            const visibleImages = combined.filter(img => !img.isDelete);
            if (visibleImages.length > 0 && !visibleImages.some(img => img.isDefaultMedia)) {
                const firstVisible = visibleImages[0];
                return combined.map(img => ({ ...img, isDefaultMedia: img.id === firstVisible.id }));
            }
            return combined;
        });
    }, []);

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            addImageFiles(e.target.files);
            e.target.value = '';
        }
    };

    const handleDeleteImage = (id: number) => {
        setUploadedImages(prev => {
            // id < 0 means a newly uploaded image  → remove from list entirely
            // id > 0 means an existing server image → soft-delete (mark isDelete = true)
            const updated = id < 0
                ? prev.filter(img => img.id !== id)
                : prev.map(img => img.id === id ? { ...img, isDelete: true } : img);

            // Reassign default to first visible image if the deleted one was default
            const visibleImages = updated.filter(img => !img.isDelete);
            if (visibleImages.length > 0 && !visibleImages.some(img => img.isDefaultMedia)) {
                const firstVisibleId = visibleImages[0].id;
                return updated.map(img => ({ ...img, isDefaultMedia: img.id === firstVisibleId }));
            }
            return updated;
        });
    };

    const handleSetDefault = (id: number) => {
        setUploadedImages(prev =>
            prev.map(img => ({ ...img, isDefaultMedia: img.id === id }))
        );
    };

    const handleDragOverArea = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeaveArea = () => setIsDragOver(false);

    const handleDropArea = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addImageFiles(e.dataTransfer.files);
        }
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setUploadedImages((items) => {
                const oldIndex = items.findIndex((item) => item.id.toString() === active.id);
                const newIndex = items.findIndex((item) => item.id.toString() === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const openCropModal = async (img: UploadedImage) => {
        completedCropRef.current = null;

        // For existing server images the previewUrl is a remote URL which would
        // taint the canvas (CORS). Fetch it as a local blob URL instead.
        let srcUrl = img.previewUrl;
        const isExternalUrl = img.previewUrl.startsWith('http://') || img.previewUrl.startsWith('https://');
        if (isExternalUrl) {
            try {
                setIsCropLoading(true);
                const response = await fetch(img.previewUrl);
                if (!response.ok) throw new Error('Failed to fetch image');
                const blob = await response.blob();
                srcUrl = URL.createObjectURL(blob);
            } catch {
                showMessage('Crop', 'Unable to load image for cropping. The server may not allow cross-origin access.', 'error');
                setIsCropLoading(false);
                return;
            } finally {
                setIsCropLoading(false);
            }
        }

        setCropModal({
            imageId: img.id,
            srcUrl,
            crop: { unit: '%', x: 10, y: 10, width: 80, height: 80 },
        });
    };

    const getCroppedImg = (image: HTMLImageElement, pixelCrop: PixelCrop): Promise<{ url: string, file: File }> => {
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = Math.floor(pixelCrop.width * scaleX);
        canvas.height = Math.floor(pixelCrop.height * scaleY);
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(
            image,
            Math.floor(pixelCrop.x * scaleX),
            Math.floor(pixelCrop.y * scaleY),
            Math.floor(pixelCrop.width * scaleX),
            Math.floor(pixelCrop.height * scaleY),
            0,
            0,
            Math.floor(pixelCrop.width * scaleX),
            Math.floor(pixelCrop.height * scaleY),
        );
        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (!blob) { reject(new Error('Canvas is empty')); return; }
                const file = new File([blob], 'cropped.webp', { type: 'image/webp' });
                resolve({ url: URL.createObjectURL(blob), file });
            }, 'image/webp', 0.95);
        });
    };

    const handleSaveCrop = async () => {
        if (!cropModal || !cropImgRef.current || !completedCropRef.current) return;
        try {
            const { url, file } = await getCroppedImg(cropImgRef.current, completedCropRef.current);
            const isExisting = cropModal.imageId > 0;

            if (isExisting) {
                // For existing server images: soft-delete the original and insert the
                // cropped result as a brand-new upload at the same position
                setUploadedImages(prev => {
                    const idx = prev.findIndex(img => img.id === cropModal.imageId);
                    if (idx === -1) return prev;

                    const wasDefault = prev[idx].isDefaultMedia;
                    const newImg: UploadedImage = {
                        id: -(Date.now()),       // new negative id = treated as fresh upload
                        file,
                        previewUrl: url,
                        isDefaultMedia: wasDefault,
                        isDelete: false,
                    };

                    // Replace in-place: mark old as deleted, insert new at same index
                    const updated = [...prev];
                    updated[idx] = { ...updated[idx], isDelete: true, isDefaultMedia: false };
                    updated.splice(idx, 0, newImg);  // insert before the deleted entry
                    return updated;
                });
            } else {
                // For newly uploaded images: just update the preview + file in-place
                setUploadedImages(prev =>
                    prev.map(img =>
                        img.id === cropModal.imageId ? { ...img, previewUrl: url, file } : img
                    )
                );
            }

            setCropModal(null);
        } catch {
            showMessage('Crop', 'Failed to crop image', 'error');
        }
    };

    return (
        <><Toast ref={toast} position="top-center" />
            <div className="col-12  flex align-items-stretch flex-wrap">
                <div className="flex flex-1 bg-white align-items-center justify-content-center">
                    <form onSubmit={formik.handleSubmit}>
                        {!hideTitle && (
                            <h5 className='border-round mb-3 order-top-color p-2 panel-header-text text-center'>
                                {isUpdate ? t('update_product') : t('register_product')}
                            </h5>
                        )}
                        <div className="p-fluid formgrid grid">
                            <div className="col-12">
                                <div className="grid">
                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="categoryID" className="label-semi-bold">{t('product_category')}<span className="required">*</span></label>
                                            <Dropdown
                                                id="categoryID"
                                                name="categoryID"
                                                onBlur={formik.handleBlur}
                                                className={`pl-0 ${classNames({ 'p-invalid': formik.touched.categoryID && formik.errors.categoryID })}`}
                                                value={formik.values.categoryID}
                                                onChange={(e) => {
                                                    formik.setFieldValue('categoryID', e.value);
                                                    formik.setFieldValue('subCategoryID', null); // Reset subCategory to enforce validation
                                                    formik.setFieldTouched('categoryID', true, false);
                                                    categoryOnChange(e);
                                                }}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                options={categories}
                                                optionLabel="name"
                                                optionValue="categoryID"
                                                placeholder="Select Category"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="subCategoryID" className="label-semi-bold">{t('product_sub_category')}<span className="required">*</span></label>
                                            <Dropdown
                                                id="subCategoryID"
                                                name="subCategoryID"
                                                onBlur={formik.handleBlur}
                                                className={`pl-0 ${classNames({ 'p-invalid': formik.touched.subCategoryID && formik.errors.subCategoryID })}`}
                                                value={formik.values.subCategoryID}
                                                onChange={(e) => {
                                                    formik.setFieldValue('subCategoryID', e.value);
                                                    formik.setFieldTouched('subCategoryID', true, false);
                                                    subCategoryOnChange(e);
                                                }}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                options={filteredSubCategories}
                                                optionLabel="name"
                                                optionValue="subCategoryID"
                                                placeholder="Select Sub Category"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label className="block mb-1 font-medium">Vendor</label>
                                            <Dropdown
                                                id="vendorID"
                                                name="vendorID"
                                                onBlur={formik.handleBlur}
                                                className={classNames({ "p-invalid": formik.touched.vendorID && formik.errors.vendorID })}
                                                value={formik.values.vendorID}
                                                options={vendorList}
                                                onChange={(e) => {
                                                    formik.setFieldValue("vendorID", e.value);
                                                    formik.setFieldTouched("vendorID", true, false);
                                                }}
                                                placeholder="Select Vendor"
                                                filter
                                                filterBy="label"
                                                showClear
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="measurementTypeID" className="label-semi-bold">{t('measurement_Type')}<span className="required">*</span></label>
                                            <Dropdown
                                                id="measurementTypeID"
                                                name="measurementTypeID"
                                                onBlur={formik.handleBlur}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.touched.measurementTypeID && formik.errors.measurementTypeID })}`}
                                                value={formik.values.measurementTypeID}
                                                onChange={(e) => {
                                                    formik.setFieldValue('measurementTypeID', e.value);
                                                    formik.setFieldTouched('measurementTypeID', true, false);
                                                    measureTypeOnChange(e);
                                                }}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                options={measurementTypeItems}
                                                optionLabel="name"
                                                optionValue="measurementTypeID"
                                                placeholder="Select Measurement Type"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 lg:col-6 mb-2 md:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="name" className="label-semi-bold">{t('product_name')}<span className="required">*</span></label>
                                            <span className="p-input-icon-left">
                                                <InputText type="text" id="name" name="name"
                                                    value={formik.values.name}
                                                    onBlur={formik.handleBlur}
                                                    onChange={(e) => {
                                                        const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                                                        formik.setFieldValue('name', cleanedValue);
                                                    }}
                                                    className={`pl-1 ${classNames({ 'p-invalid': formik.touched.name && formik.errors.name })} tracking-normal whitespace-normal`}
                                                    placeholder={t('Product Name')}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                />
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col-12 lg:col-6 mb-2 md:col-6 mb-3 px-3">
                                        <label htmlFor="unitPrice" className="label-semi-bold">{t('unit_price')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputNumber
                                                id="unitPrice"
                                                name="unitPrice"
                                                value={formik.values.unitPrice}
                                                onBlur={formik.handleBlur}
                                                onValueChange={e => {
                                                    formik.setFieldValue('unitPrice', e.value);
                                                }}
                                                mode="decimal"
                                                minFractionDigits={2}
                                                maxFractionDigits={2}
                                                placeholder="Enter Unit Price"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                                            />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="usage" className="label-semi-bold">{t('usage')}</label>
                                            <span className="p-input-icon-left">
                                                <InputTextarea autoResize id="usage" name="usage"
                                                    value={formik.values.usage}
                                                    onChange={(e) => {
                                                        const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                                                        formik.setFieldValue('usage', cleanedValue);
                                                    }}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    className={`pl-1 ${classNames({ 'p-invalid': formik.errors.usage })}`}
                                                    placeholder="Usage" rows={4} cols={30} />
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="description" className="label-semi-bold">{t('description')}</label>
                                            <span className="p-input-icon-left">
                                                <InputTextarea autoResize id="description" name="description"
                                                    value={formik.values.description}
                                                    onChange={(e) => {
                                                        const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                                                        formik.setFieldValue('description', cleanedValue);
                                                    }}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    className={`pl-1 ${classNames({ 'p-invalid': formik.errors.description })}`}
                                                    placeholder="Enter Description" rows={4} cols={30} />
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="code" className="label-semi-bold">{t('code')}</label>
                                            <span className="p-input-icon-left">
                                                <InputText type="text" id="code" name="code"
                                                    value={formik.values.code}
                                                    keyfilter="alphanum"
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    onChange={(e) => {
                                                        const cleanedValue = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                                        formik.setFieldValue('code', cleanedValue);
                                                    }}
                                                    className={`pl-1 ${classNames({ 'p-invalid': formik.errors.code })}`}
                                                    placeholder="Enter Code" />
                                            </span>
                                        </div>
                                    </div>

                                    <div className="field col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="weight" className="label-semi-bold">{t('weight')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputNumber
                                                id="weight"
                                                name="weight"
                                                value={formik.values.weight}
                                                onBlur={formik.handleBlur}
                                                onValueChange={e => {
                                                    formik.setFieldValue('weight', e.value);
                                                }}
                                                mode="decimal"
                                                min={0}
                                                maxFractionDigits={2}
                                                placeholder="Weight"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="volume" className="label-semi-bold">{t('Volume')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputNumber
                                                id="volume"
                                                name="volume"
                                                value={formik.values.volume}
                                                onBlur={formik.handleBlur}
                                                onValueChange={e => {
                                                    formik.setFieldValue('volume', e.value);
                                                }}
                                                placeholder="Enter Volume"
                                                maxFractionDigits={2}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="measurement" className="label-semi-bold">{t('Measurement')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <Dropdown
                                                id="measurement"
                                                name="measurement"
                                                value={formik.values.measurement}
                                                options={[
                                                    { label: "ml", value: "ml" },
                                                    { label: "gms", value: "gms" }
                                                ]}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                onBlur={formik.handleBlur}
                                                onChange={(e) => {
                                                    formik.setFieldValue('measurement', e.value);
                                                    formik.setFieldTouched('measurement', true, false);
                                                }}
                                                placeholder="Select Unit (ml/gms)"
                                                className={`pl-1 w-full ${classNames({ 'p-invalid': formik.touched.measurement && formik.errors.measurement })}`}
                                            />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="minOrderQty" className="label-semi-bold">{t('min_order_qty')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputNumber
                                                id="minOrderQty"
                                                name="minOrderQty"
                                                value={formik.values.minOrderQty}
                                                onBlur={formik.handleBlur}
                                                onValueChange={e => {
                                                    formik.setFieldValue('minOrderQty', e.value);
                                                }}
                                                mode="decimal"
                                                min={0}
                                                placeholder="Enter Min Quantity"
                                                maxFractionDigits={2}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="orderMultiples" className="label-semi-bold">{t('order_multiples')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="orderMultiples" id="orderMultiples"
                                                value={formik.values.orderMultiples}
                                                keyfilter="pnum"
                                                min={0} max={100}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                                                        formik.setFieldValue('orderMultiples', value);
                                                    }
                                                }}
                                                onBlur={formik.handleBlur}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.touched.orderMultiples && formik.errors.orderMultiples })}`}
                                                placeholder="Order Multiples" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="gst" className="label-semi-bold">{t('gst')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="gst" id="gst"
                                                value={formik.values.gst}
                                                keyfilter="pnum"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || /^-?\d{0,2}(\.\d{0,2})?$/.test(value)) {
                                                        formik.setFieldValue('gst', value);
                                                    }
                                                }}
                                                maxLength={5}
                                                onBlur={formik.handleBlur}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.touched.gst && formik.errors.gst })}`}
                                                placeholder="GST" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="hsN_Code" className="label-semi-bold">{t('hsn_code')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="hsN_Code" id="hsN_Code"
                                                value={formik.values.hsN_Code}
                                                onChange={formik.handleChange}
                                                keyfilter="alphanum"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.hsN_Code })}`}
                                                placeholder="HSN Code" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="subscriptionAvailable" className="label-semi-bold">{t('subscription_available')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                <Checkbox
                                                    id="subscriptionAvailable"
                                                    name="subscriptionAvailable"
                                                    onChange={(e) => {
                                                        formik.setFieldValue('subscriptionAvailable', e.checked);
                                                    }}
                                                    checked={formik.values.subscriptionAvailable}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                />
                                                <label htmlFor="subscriptionAvailable">{t('subscription_available')} </label>
                                            </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="activeStartDate" className="label-semi-bold">{t('active_start_date')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText
                                                type="date"
                                                name="activeStartDate"
                                                id="activeStartDate"
                                                value={formik.values.activeStartDate}
                                                onChange={formik.handleChange}
                                                onFocus={(e) => e.target.showPicker?.()}
                                                onKeyDown={(e) => e.preventDefault()}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.activeStartDate })}`}
                                                placeholder="Enter Active Start Date"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />

                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="activeEndDate" className="label-semi-bold">{t('active_end_date')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText
                                                type="date"
                                                name="activeEndDate"
                                                id="activeEndDate"
                                                value={formik.values.activeEndDate}
                                                min={formik.values.activeStartDate || ""}   // ✅ This prevents selecting a lower date
                                                onFocus={(e) => e.target.showPicker?.()}
                                                onKeyDown={(e) => e.preventDefault()}
                                                onChange={formik.handleChange}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.activeEndDate })}`}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                placeholder="Enter Active End Date"
                                            />

                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="leadTime" className="label-semi-bold">{t('lead_time')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText type="date" name="leadTime" id="leadTime"
                                                value={formik.values.leadTime}
                                                onFocus={(e) => e.target.showPicker?.()}
                                                onKeyDown={(e) => e.preventDefault()}
                                                onChange={formik.handleChange}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.leadTime })}`}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                placeholder="Lead Time" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="canExchange" className="label-semi-bold">{t('can_exchange')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                <Checkbox
                                                    id="canExchange"
                                                    name="canExchange"
                                                    onChange={(e) => {
                                                        formik.setFieldValue('canExchange', e.checked);
                                                    }}
                                                    checked={formik.values.canExchange}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                />
                                                <label htmlFor="canExchange">{t('can_exchange')} </label>
                                            </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="canReturn" className="label-semi-bold">{t('can_return')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                <Checkbox
                                                    id="canReturn"
                                                    name="canReturn"
                                                    onChange={(e) => {
                                                        formik.setFieldValue('canReturn', e.checked);
                                                    }}
                                                    checked={formik.values.canReturn}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                />
                                                <label htmlFor="canReturn">{t('can_return')} </label>
                                            </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="courierAvailable" className="label-semi-bold">{t('Is Courier Available?')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                <Checkbox
                                                    id="courierAvailable"
                                                    name="courierAvailable"
                                                    onChange={(e) => {
                                                        formik.setFieldValue('courierAvailable', e.checked);
                                                    }}
                                                    checked={formik.values.courierAvailable}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                />
                                                <label htmlFor="courierAvailable">{t('courier_available')} </label>
                                            </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="shelfLife" className="label-semi-bold">{t('shelf_life')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="shelfLife" id="shelfLife"
                                                value={formik.values.shelfLife}
                                                onChange={(e) => {
                                                    const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                                                    formik.setFieldValue('shelfLife', cleanedValue);
                                                }}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.shelfLife })}`}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                placeholder="Shelf Life" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="inStock" className="label-semi-bold">{t('in_stock')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="inStock" id="inStock"
                                                value={formik.values.inStock}
                                                keyfilter="pint"
                                                onChange={formik.handleChange}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.inStock })}`}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                placeholder="In Stock" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="isDeleted" className="label-semi-bold">{t('is_deleted')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                <Checkbox
                                                    id="isDeleted"
                                                    name="isDeleted"
                                                    onChange={(e) => {
                                                        formik.setFieldValue('isDeleted', e.checked);
                                                    }}
                                                    checked={formik.values.isDeleted}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                />
                                                <label htmlFor="isDeleted">{t('is_deleted')} </label>
                                            </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="isHomeDeliveryAvailable" className="label-semi-bold">{t('Home Delivery')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                <Checkbox
                                                    id="isHomeDeliveryAvailable"
                                                    name="isHomeDeliveryAvailable"
                                                    onChange={(e) => {
                                                        formik.setFieldValue('isHomeDeliveryAvailable', e.checked);
                                                    }}
                                                    checked={formik.values.isHomeDeliveryAvailable}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                />
                                                <label htmlFor="isHomeDeliveryAvailable">{t('Home Delivery')} </label>
                                            </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="forCustomer" className="label-semi-bold">{t('For Shopping')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                <Checkbox
                                                    id="forCustomer"
                                                    name="forCustomer"
                                                    onChange={(e) => {
                                                        formik.setFieldValue('forCustomer', e.checked);
                                                    }}
                                                    checked={formik.values.forCustomer}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                />
                                                <label htmlFor="forCustomer">{t('For Shopping')} </label>
                                            </div>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Multi-Image Upload Section ── */}
                        <div className="col-12 px-3 mb-3">
                            <label className="label-semi-bold d-block mb-2">Product Images</label>

                            {/* Drop zone / select area */}
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={handleDragOverArea}
                                onDragLeave={handleDragLeaveArea}
                                onDrop={handleDropArea}
                                style={{
                                    border: `2px dashed ${isDragOver ? '#4caf50' : '#adb5bd'}`,
                                    borderRadius: '8px',
                                    padding: '24px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: isDragOver ? '#f0fff4' : '#fafafa',
                                    transition: 'all 0.2s ease',
                                    marginBottom: '16px',
                                }}
                            >
                                <i className="pi pi-cloud-upload" style={{ fontSize: '2rem', color: '#6c757d', marginBottom: '8px', display: 'block' }} />
                                <span style={{ color: '#6c757d', fontSize: '0.95rem' }}>
                                    {isDragOver
                                        ? 'Drop images here…'
                                        : 'Click to select images or drag & drop them here'}
                                </span>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg, image/png, image/webp"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handleImageUpload}
                                />
                            </div>

                            {/* Image preview grid */}
                            {uploadedImages.filter(img => !img.isDelete).length > 0 && (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                                        gap: '16px',
                                    }}>
                                        <SortableContext
                                            items={uploadedImages.map(d => d.id.toString())}
                                            strategy={horizontalListSortingStrategy}
                                        >
                                            {uploadedImages.filter(img => !img.isDelete).map(img => (
                                                <SortableImageItem
                                                    key={img.id}
                                                    img={img}
                                                    handleDeleteImage={handleDeleteImage}
                                                    handleSetDefault={handleSetDefault}
                                                    openCropModal={openCropModal}
                                                />
                                            ))}
                                        </SortableContext>
                                    </div>
                                </DndContext>
                            )}
                        </div>

                        <Button type="submit" label="Save" className="right-align" > </Button>
                    </form>
                </div>
            </div>

            {/* ── Crop loading overlay ── */}
            {isCropLoading && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: 'rgba(0,0,0,0.55)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '12px',
                }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem', color: '#fff' }} />
                    <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 500 }}>Loading image…</span>
                </div>
            )}

            {/* ── Crop Modal ── */}
            {cropModal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: 'rgba(0,0,0,0.72)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '640px',
                        width: '100%',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                    }}>
                        {/* Modal header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontWeight: 700, fontSize: '1.05rem', color: '#212529' }}>
                                <i className="pi pi-pencil" style={{ marginRight: '8px', color: '#2196f3' }} />
                                Crop Image
                            </span>
                            <button
                                type="button"
                                onClick={() => setCropModal(null)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    color: '#6c757d',
                                    lineHeight: 1,
                                }}
                            >
                                <i className="pi pi-times" />
                            </button>
                        </div>

                        {/* Crop area */}
                        <div style={{
                            maxHeight: '420px',
                            overflowY: 'auto',
                            display: 'flex',
                            justifyContent: 'center',
                            background: '#1a1a1a',
                            borderRadius: '8px',
                            padding: '8px',
                        }}>
                            <ReactCrop
                                crop={cropModal.crop}
                                onChange={(_, pct) => setCropModal(prev => prev ? { ...prev, crop: pct } : null)}
                                onComplete={c => { completedCropRef.current = c; }}
                                keepSelection
                            >
                                <img
                                    ref={cropImgRef}
                                    src={cropModal.srcUrl}
                                    alt="crop-preview"
                                    crossOrigin="anonymous"
                                    style={{ maxWidth: '100%', maxHeight: '400px', display: 'block' }}
                                />
                            </ReactCrop>
                        </div>

                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#6c757d' }}>
                            Drag the selection handles to define the crop area, then click <strong>Save Crop</strong>.
                        </p>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => setCropModal(null)}
                                style={{
                                    padding: '8px 20px',
                                    borderRadius: '6px',
                                    border: '1px solid #ced4da',
                                    background: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '0.88rem',
                                    color: '#495057',
                                }}
                            >Cancel</button>
                            <button
                                type="button"
                                onClick={handleSaveCrop}
                                style={{
                                    padding: '8px 20px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: '#2196f3',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: '0.88rem',
                                }}
                            >
                                <i className="pi pi-check" style={{ marginRight: '6px' }} />
                                Save Crop
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ProductInfo